import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Dropdown, Row } from "react-bootstrap";
import { BzDropdown, BzMasterAgGrid } from "../../components";
import { BEST5_INDEX, GLOBAL_FUNCTION_TYPES, OPTION_CHAIN, OPTION_CHAIN_DROPDOWN, OPTION_CHAIN_GRID_INDEX, TL_INDEX } from "../../utils/global_variables";
import { useDispatch, useSelector } from "react-redux";
import { clearOptionChain, updateDateSelection, updateOptionChainDummyData, updateOptionChainLoading, updateOptionChainScrips } from "../../redux/actions/optionChain";
import { useDebounce, useUnload, useWFHMessages } from "../../hooks";
import { findIndexForValueBetween, globalFunctionsEventDispatcher, isGuestUser, modifySymbol, twoDecimalValueChecker } from "../../utils/global_functions";
import OptionChainToolTip from "./OptionChainToolTip";
import { sendBestFiveRequest, sendTouchlineRequest } from "../../apis/web_socket";
import { _dateDiffernece } from "../../controllers";
import { _calculateGridDataForCall, _calculateGridDataForPut, _optionContract } from "../../controllers/optionChain";
import TogglerAndCheckBox from "./TogglerAndCheckBox";
import { LoginLoader } from "../Login";
import { getWfhBestFiveLastTick, getWfhTouchlineLastTick } from "../../redux/actions/wfh";
import OptionChainToaster from "./OptionChainToaster";

/*
SLL -- SINGLY LINKIED LIST
*/

/*
Below class is a Singly Linked List just to store
functions that gets added at tail and gets removeda at head
as array takes time and some computation for re arranging of indexes
to avoid that Singly Linked list is being used
*/
class ListNode {
  constructor(value) {
    this.value = value;
    this.next = null;
  }
}


/*
Below class is a functionCallQueue  which is used to store and call the functions
which are enqued or dequed accordingly without any priorites
*/
class FunctionQueue {
  constructor() {
    this.head = null;
    this.tail = null;
    this.isProcessing = false;
  }

  /*
  Below method enqueues or adds function to SSl and starts processign the queue
  if queue has any functions in it
*/
  enqueue(func) {
    const newNode = new ListNode(func);
    if (this.tail) {
      this.tail.next = newNode;
    }
    this.tail = newNode;
    if (!this.head) {
      this.head = newNode;
    }
    this.processQueue();
  }

  /*
 Below method runs the functions one by one that are added to the SLL
 */
  async processQueue() {
    if (this.isProcessing) return; //Prevents multiple simultaneous executions
    this.isProcessing = true;
    while (this.head) {
      const currentFunction = this.head.value;
      await currentFunction();
      this.head = this.head.next;
      if (!this.head) {
        this.tail = null;
      }
    }
    this.isProcessing = false;
  }
}

const OptionChainGrid = ({ halfHeight = false, sideOverview = false }) => {


  const OPTION_CHAIN_SCREEN_REF = useRef();
  const dateref = useRef();

  const dispatch = useDispatch();
  const arrLegDataForOptionChain = useSelector(state => state?.optionChain?.arrLegDataForOptionChain);
  const optionChainDummyData = useSelector(state => state?.optionChain?.optionChainDummyData);
  const arrStrikePrices = useSelector(state => state?.optionChain?.arrStrikePrices);
  const arrDistinctDates = useSelector(state => state?.optionChain?.arrDistinctDatesForOptionChain);
  const objOptionChainPayload = useSelector(state => state?.optionChain?.objOptionChainPayload);
  const isLoadingOptionChian = useSelector(state => state?.optionChain?.isLoadingOptionChian);
  const scripOverviewData = useSelector(state => state?.marketOverview?.scripOverviewData);
  const [spotPrice, setSpotPrice] = useState(null);
  const [selectedExpiry, setSelectedExpiry] = useState({});
  const [objScripRequest, setObjScripsRequest] = useState({});
  const [selectedContract, setSelectedContract] = useState('ALL');
  const [objGridVisibleRows, setObjGridVisibleRows] = useState({});
  const [contractDropdown, setContractDropdown] = useState();
  const [strActiveOption, setStrActiveOption] = useState(OPTION_CHAIN.CALL_PUT);
  const [onLoadStrikePrice, setOnLoadStrikePrice] = useState(null);
  const queue = useMemo(() => new FunctionQueue(), []);
  /*
   inside any useEffect queue shoudl be given as dependency this make componenet to rerender continously
*/


  /*
 Below useEffect updates the required state back to null
 */
  const handleLoadingStrikePrice = useCallback(() => {
    if (onLoadStrikePrice) {
      setOnLoadStrikePrice(null);
    }
    setSpotPrice(null);
  }, [onLoadStrikePrice]);
  useUnload(() => {
    handleLoadingStrikePrice();
  });
  useEffect(() => {
    return () => {
      handleLoadingStrikePrice();
    };
  }, []);

  useEffect(() => {
    if (isLoadingOptionChian && spotPrice !== null) {
      setSpotPrice(null);
    }
  }, [isLoadingOptionChian, spotPrice]);

  useEffect(() => {
    return () => {
      // reset option chain data here and unsubscribe data here
      dispatch(clearOptionChain());
      let arrDataToUnsubScribe = Object?.keys(objScripRequest)?.map((eachToken) => {
        return { exchangeId: Number(eachToken?.split("_")[0]), token: Number(eachToken?.split("_")[1]), isSubscribe: false };
      });
      sendBestFiveRequest({
        exchangeId: Number(objOptionChainPayload?.convertedId),
        token: Number(objOptionChainPayload?.assetToken),
        isSubscribe: false,
      });
      sendTouchlineRequest(arrDataToUnsubScribe);
    };

  }, []);
  useEffect(() => {
    if (objOptionChainPayload?.location === OPTION_CHAIN.SIDE_OVERVIEW) {
      setSpotPrice(null);
      setTimeout(() => {
        handleLastTickOfSelectedOption();
        setTimeout(() => {
          OPTION_CHAIN_SCREEN_REF?.current?.api?.ensureColumnVisible(OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE?.toString(), 'middle');
          queue.enqueue(findClosestArrayOfSpotPrice);
        }, 500);
      }, 200);
    }
  }, [scripOverviewData, objOptionChainPayload, arrStrikePrices, queue]);

  useEffect(() => {
    setSelectedExpiry(objOptionChainPayload?.expiryDate);
  }, [objOptionChainPayload]);
  /*
  Below useEffect updates the visibelScrips in redux so even incase the unmounting not happens
  so when closing unSubscribing the data will happen on click on close of modals
  */
  useEffect(() => {
    if (Object?.keys(objScripRequest)?.length > 0) {
      useDebounce(() => {
        dispatch(updateOptionChainScrips({ ...objScripRequest }));;
      }, 200);
    }
  }, [objScripRequest, dispatch]);



  /*
  Below variabels is used to send data to grid as row data
  */
  const arrMemoMisedOptionChainData = useMemo(() => {
    if (optionChainDummyData?.length > 0) {
      return optionChainDummyData;
    } else {
      return [];
    }
  }, [optionChainDummyData]);

  /*
  Below component is used for Ltp  columns for both CALL and PUT
  */
  const LtpCellRender = ({ value, arrRowData, type }) => {
    switch (type) {
      case OPTION_CHAIN.CALL:
        const textColor = arrRowData[OPTION_CHAIN_GRID_INDEX.CALL_PERCENT_CHANGE_LTP]?.toString()?.split("")[0] === '+' ? 'color-positive' : 'color-negative';
        if (arrRowData[OPTION_CHAIN_GRID_INDEX.IS_CALL_DATA_PRESENT] === '-') return <div>-</div>;
        return <div className={value !== '0.00' ? textColor : ""}>{`${value}%`}</div>;
      case OPTION_CHAIN.PUT:
        const textColor1 = arrRowData[OPTION_CHAIN_GRID_INDEX.PUT_PERCENT_CHANGE_LTP]?.toString()?.split("")[0] === '+' ? 'color-positive' : 'color-negative';
        if (arrRowData[OPTION_CHAIN_GRID_INDEX.IS_PUT_DATA_PRESENT] === '-') return <div>-</div>;
        return <div className={value !== '0.00' ? textColor1 : ""}>{`${value}%`}</div>;
      default:
        return value;
    }
  };

  /*

 Below component is used for Oi Columns for Both CALL and PUT
 */
  const OiCellRenderer = ({ value = '', type, arrRowData }) => {
    const raw = value;
    let display = '';
    if (raw && typeof raw === 'string') {
      // Split on space to ignore the initial value and take the parentheses part (original value is "12 (+0.25)")
      const parts = raw.split(' ');
      if (parts.length > 1) {
        // parts[1] is like '(+0.25)'
        const withParens = parts[1];
        // Remove surrounding parentheses
        display = withParens.replace(/^\((.*)\)$/, '$1').trim();
      }
    }
    switch (type) {
      case OPTION_CHAIN.CALL:
        if (arrRowData[OPTION_CHAIN_GRID_INDEX.IS_CALL_DATA_PRESENT] === '-') return <div>-</div>;
        return <div className={value?.split(" ")[1]?.includes('+') ? 'color-positive' : ''}>{display ? display : "0.00"}</div>;
      case OPTION_CHAIN.PUT:
        if (arrRowData[OPTION_CHAIN_GRID_INDEX.IS_PUT_DATA_PRESENT] === '-') return <div>-</div>;
        return <div className={value?.split(" ")[1]?.includes('+') ? 'color-positive' : ''}>{display ? display : "0.00"}</div>;
      default:
        return value;
    }
  };

  /*
  Below variables is used for ColumnDef data for option hcain grid
  make sure no dependencies are given as it leads to rerendering
  */
  const columnDefs = useMemo(() => {
    return [
      {
        type: OPTION_CHAIN?.CALL,
        pinned: 'left', headerName: null, field: null, maxWidth: 40,
        sortable: false, menuTabs: [], lockPosition: 'left',
        cellRenderer: ({ data: arrRowData, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          <TogglerAndCheckBox arrRowData={arrRowData} {...props} />
        </OptionChainToolTip>,
        suppressMovable: true,
        suppressCellFlash: true
      },
      {
        type: OPTION_CHAIN?.CALL,
        field: OPTION_CHAIN_GRID_INDEX?.DELTA_CALL?.toString(),
        headerName: 'DELTA',
        sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.CALL_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) < parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.CALL,
        field: OPTION_CHAIN_GRID_INDEX?.VEGA_CALL?.toString(), headerName: 'VEGA', sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.CALL_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) < parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.CALL,
        field: OPTION_CHAIN_GRID_INDEX?.GAMMA_CALL?.toString(), headerName: 'GAMMA', sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.CALL_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) < parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.CALL,
        field: OPTION_CHAIN_GRID_INDEX?.THETA_CALL?.toString(), headerName: 'THETA', sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.CALL_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) < parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.CALL,
        field: OPTION_CHAIN_GRID_INDEX?.IV_CALL?.toString(), headerName: 'IV', sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.CALL_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) < parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.CALL,
        field: OPTION_CHAIN_GRID_INDEX?.VOLUME_CALL?.toString(), headerName: 'VOLUME(Lacs)', sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.CALL_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) < parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.CALL,
        field: OPTION_CHAIN_GRID_INDEX?.OI_CALL?.toString(), headerName: 'OI Change%', sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.CALL_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) < parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          <OiCellRenderer value={value} arrRowData={arrRowData} props={props} type={OPTION_CHAIN.CALL} />
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.CALL,
        field: OPTION_CHAIN_GRID_INDEX?.OI_CALL?.toString(), headerName: 'OI (Lakhs)', sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.CALL_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) < parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value?.split(" ")[0]}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.CALL,
        field: OPTION_CHAIN_GRID_INDEX?.CALL_PERCENT_CHANGE_LTP?.toString(), sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.CALL_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) < parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          <LtpCellRender value={value} arrRowData={arrRowData} props={props} type={OPTION_CHAIN.CALL} />
        </OptionChainToolTip>,
        headerName: 'CHANGE%', suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.CALL,
        field: OPTION_CHAIN_GRID_INDEX?.LTP_CALL?.toString(), sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.CALL_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) < parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>,
        headerName: 'LTP', suppressMovable: true
      },
      //All abour columsn are for calls
      //below column is for STRIKE PRICE  {value?.split(" ")[0]} 
      {
        field: OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE?.toString(), headerName: 'STRIKE', sortable: false, menuTabs: [], suppressMovable: true,
        valueFormatter: params => {
          const raw = params.value;
          return twoDecimalValueChecker(raw);
        },
      },
      //All the below columns are for put
      {
        type: OPTION_CHAIN?.PUT,
        field: OPTION_CHAIN_GRID_INDEX?.LTP_PUT?.toString(), headerName: 'LTP', menuTabs: [], sortable: false,
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.PUT_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) > parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.PUT,
        field: OPTION_CHAIN_GRID_INDEX.PUT_PERCENT_CHANGE_LTP.toString(), headerName: 'CHANGE%', menuTabs: [], sortable: false,
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.PUT_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) > parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          <LtpCellRender value={value} arrRowData={arrRowData} props={props} type={OPTION_CHAIN.PUT} />
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.PUT,
        field: OPTION_CHAIN_GRID_INDEX?.OI_PUT?.toString(), sortable: false, menuTabs: [], headerName: 'OI (Lakhs)',
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.PUT_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) > parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value?.split(" ")[0]}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.PUT,
        field: OPTION_CHAIN_GRID_INDEX?.OI_PUT?.toString(), sortable: false, menuTabs: [], headerName: 'OI Change%',
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.PUT_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) > parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          <OiCellRenderer value={value} arrRowData={arrRowData} props={props} type={OPTION_CHAIN.PUT} />
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.PUT,
        field: OPTION_CHAIN_GRID_INDEX?.VOLUME_PUT?.toString(), headerName: 'VOLUME(Lacs)', sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.PUT_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) > parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.PUT,
        field: OPTION_CHAIN_GRID_INDEX?.IV_PUT?.toString(), sortable: false, menuTabs: [], headerName: 'IV',
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.PUT_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) > parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.PUT,
        field: OPTION_CHAIN_GRID_INDEX?.THETA_PUT?.toString(), sortable: false, menuTabs: [], headerName: 'THETA',
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.PUT_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) > parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.PUT,
        field: OPTION_CHAIN_GRID_INDEX?.GAMMA_PUT?.toString(), sortable: false, menuTabs: [], headerName: 'GAMMA',
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.PUT_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) > parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.PUT,
        field: OPTION_CHAIN_GRID_INDEX?.VEGA_PUT?.toString(), sortable: false, menuTabs: [], headerName: 'VEGA',
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.PUT_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) > parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        type: OPTION_CHAIN?.PUT,
        field: OPTION_CHAIN_GRID_INDEX?.DELTA_PUT?.toString(), headerName: 'DELTA', sortable: false, menuTabs: [],
        cellClassRules: {
          "ag-cell-background-yellow": params => params?.data[OPTION_CHAIN_GRID_INDEX.PUT_MULTILEG_COLOR] === true ? true : false,
          'ag-cell-background': params => (parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE]) > parseFloat(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP])) && parseInt(params?.data[OPTION_CHAIN_GRID_INDEX?.SPOT_LTP]) !== 0,
        },
        cellRenderer: ({ data: arrRowData, value, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          {value}
        </OptionChainToolTip>, suppressMovable: true
      },
      {
        field: OPTION_CHAIN_GRID_INDEX?.SPOT_LTP?.toString(), hide: true, headerName: '',

      },
      {
        type: OPTION_CHAIN?.PUT,
        pinned: 'right', maxWidth: 40, sortable: false, menuTabs: [], lockPosition: 'right', field: null,
        cellRenderer: ({ data: arrRowData, ...props }) => <OptionChainToolTip arrRowData={arrRowData} {...props}>
          <TogglerAndCheckBox arrRowData={arrRowData} {...props} />
        </OptionChainToolTip>,
        suppressMovable: true
      }
    ];
  }, []);

  /*
  Below Function is mandatory for grid for returning IDs so implementing changes will be smooth
  and mandatory if grid has data changes will be applied effectively
  */
  const getRowId = useCallback((params) => {
    if (params?.data) {
      return params?.data[OPTION_CHAIN_GRID_INDEX?.ID_FOR_OPTION_CHAIN_GRID]?.toString();
    } else { }
  }, []);

  /*
Below function handles the live data that are used for option chain and data conversion
*/
  const handleLiveData = useCallback((arrLiveData) => {
    if (objGridVisibleRows[arrLiveData[TL_INDEX.SEGMENT_ID] + '_' + arrLiveData[TL_INDEX.TOKEN] + '_CALL']) {
      //call scrip
      const gridRowData = OPTION_CHAIN_SCREEN_REF?.current?.api?.getDisplayedRowAtIndex(objGridVisibleRows[arrLiveData[TL_INDEX.SEGMENT_ID] + '_' + arrLiveData[TL_INDEX.TOKEN] + '_CALL'][OPTION_CHAIN_GRID_INDEX.ID_FOR_OPTION_CHAIN_GRID]);
      let numDateDifference = _dateDiffernece(gridRowData?.data[OPTION_CHAIN_GRID_INDEX.CALL_NEXPIRY_DATE])?.data;
      let numStrikePrice = gridRowData?.data[OPTION_CHAIN_GRID_INDEX.CALL_NSTRIKE_PRICE];
      if (numDateDifference && numStrikePrice) {
        let data = _calculateGridDataForCall(
          arrLiveData[TL_INDEX.REAL_LAST_TRADE_PRICE] ? arrLiveData[TL_INDEX.LAST_TRADE_PRICE] : arrLiveData[TL_INDEX.REAL_LAST_TRADE_PRICE],
          arrLiveData[TL_INDEX.CLOSE_PRICE],
          arrLiveData[TL_INDEX.OPEN_INTEREST],
          numDateDifference,
          numStrikePrice,
          "CE",
          arrLiveData[TL_INDEX.SEGMENT_ID],
          arrLiveData[TL_INDEX.TOKEN],
          spotPrice ? parseInt(spotPrice) : 0.00,
          parseFloat(gridRowData?.data[OPTION_CHAIN_GRID_INDEX.LTP_CALL]),
          arrLiveData[TL_INDEX.REAL_NET_CHANGE_FROM_PRE_CLOSE],
          arrLiveData[TL_INDEX.REAL_ABSOLUTE_NET_CHANGE_FROM_PRE_CLOSE],
          arrLiveData[TL_INDEX.SELL_QTY],
          arrLiveData[TL_INDEX.BUY_PRICE],
          arrLiveData[TL_INDEX.SELL_PRICE],
          gridRowData?.data,
          arrLiveData[TL_INDEX.OPEN_INTEREST_PERCENT],
          arrLiveData[TL_INDEX.VOLUME],
          gridRowData?.data
        )?.data;
        if (data) {
          OPTION_CHAIN_SCREEN_REF?.current?.api?.applyTransaction({
            update: [data]
          });
        }

      } else { }

    } else if (objGridVisibleRows[arrLiveData[TL_INDEX.SEGMENT_ID] + '_' + arrLiveData[TL_INDEX.TOKEN] + '_PUT']) {
      // if PUT // OR CALL_PUT then run this else in PUT selected unneccesary to run this in CALL not visible
      const gridRowData = OPTION_CHAIN_SCREEN_REF?.current?.api?.getDisplayedRowAtIndex(objGridVisibleRows[arrLiveData[TL_INDEX.SEGMENT_ID] + '_' + arrLiveData[TL_INDEX.TOKEN] + '_PUT'][OPTION_CHAIN_GRID_INDEX.ID_FOR_OPTION_CHAIN_GRID]);
      let numDateDifference = _dateDiffernece(gridRowData?.data[OPTION_CHAIN_GRID_INDEX.PUT_NEXPIRY_DATE])?.data;
      let numStrikePrice = gridRowData?.data[OPTION_CHAIN_GRID_INDEX.PUT_NSTRIKE_PRIE];

      if (numDateDifference && numStrikePrice) {
        let data = _calculateGridDataForPut(
          arrLiveData[TL_INDEX.REAL_LAST_TRADE_PRICE] ? arrLiveData[TL_INDEX.LAST_TRADE_PRICE] : arrLiveData[TL_INDEX.REAL_LAST_TRADE_PRICE],
          arrLiveData[TL_INDEX.CLOSE_PRICE],
          arrLiveData[TL_INDEX.OPEN_INTEREST],
          numDateDifference,
          numStrikePrice,
          "PE",
          arrLiveData[TL_INDEX.SEGMENT_ID],
          arrLiveData[TL_INDEX.TOKEN],
          spotPrice ? parseInt(spotPrice) : 0.00,
          parseFloat(gridRowData?.data[OPTION_CHAIN_GRID_INDEX.LTP_PUT])
          , arrLiveData[TL_INDEX.REAL_NET_CHANGE_FROM_PRE_CLOSE],
          arrLiveData[TL_INDEX.REAL_ABSOLUTE_NET_CHANGE_FROM_PRE_CLOSE],
          arrLiveData[TL_INDEX.SELL_QTY],
          arrLiveData[TL_INDEX.BUY_PRICE],
          arrLiveData[TL_INDEX.SELL_PRICE],
          gridRowData?.data,
          arrLiveData[TL_INDEX.OPEN_INTEREST_PERCENT],
          arrLiveData[TL_INDEX.VOLUME],
          gridRowData?.data)?.data;
        if (data) {
          OPTION_CHAIN_SCREEN_REF?.current?.api?.applyTransaction({
            update: [data]
          });
        }

      }

    } else { }
  }, [objGridVisibleRows, spotPrice]);

  /*
Below function handles the lastTick data for visible row on load or on scroll
*/
  const handleVisibleRowsLastTick = useCallback((arrScrips) => {
    const lastTickData = dispatch(getWfhTouchlineLastTick());
    arrScrips?.forEach((eachScrip) => {
      if (lastTickData[eachScrip?.exchangeId + '_' + eachScrip?.token]?.length > 0) {
        handleLiveData(lastTickData[eachScrip?.exchangeId + '_' + eachScrip?.token]);
      }
    });
  }, [handleLiveData, dispatch]);

  /*
Below function applies spot prices to Visiblerows on load and on scroll so backgroudn yellow colors also will be given only for
Visible rows instead rendering for all cells
*/
  const applySpotPriceToVisibleRows = useCallback(() => {
    let arrVisbelRows = OPTION_CHAIN_SCREEN_REF?.current?.api?.getRenderedNodes()?.map((eachNode) => {
      eachNode.data[OPTION_CHAIN_GRID_INDEX.SPOT_LTP] = spotPrice;
      return eachNode?.data;
    });
    if (arrVisbelRows?.length > 0) {
      OPTION_CHAIN_SCREEN_REF?.current?.api?.applyTransaction({
        update: [[...arrVisbelRows]]
      });
    }
  }, [spotPrice]);

  /*
Below function handles the subscription logic for visible rows in grid
*/
  const handleVisibleRowsSubscription = useCallback(() => {
    let arrWithKeys = [];
    let arrRowsVisibleInPut = [];
    let objRenderedScrips = {};
    let arrRowsVisibleInCall = OPTION_CHAIN_SCREEN_REF?.current?.api?.getRenderedNodes()?.map((eachNode) => {
      let id1 = eachNode?.data[OPTION_CHAIN_GRID_INDEX.CALL_SEGMENT_ID_TOKEN]?.includes('_') ? eachNode?.data[OPTION_CHAIN_GRID_INDEX.CALL_SEGMENT_ID_TOKEN]?.split('_')[0] + '_' + eachNode?.data[OPTION_CHAIN_GRID_INDEX.CALL_SEGMENT_ID_TOKEN]?.split('_')[1] : eachNode?.data[OPTION_CHAIN_GRID_INDEX.CALL_SEGMENT_ID_TOKEN];
      objRenderedScrips[id1 + '_CALL'] = eachNode?.data;
      arrWithKeys?.push(id1);
      let id2 = eachNode?.data[OPTION_CHAIN_GRID_INDEX.PUT_SEGMENT_ID_TOKE]?.includes('_') ? eachNode?.data[OPTION_CHAIN_GRID_INDEX.PUT_SEGMENT_ID_TOKE]?.split("_")[0] + '_' + eachNode?.data[OPTION_CHAIN_GRID_INDEX.PUT_SEGMENT_ID_TOKE]?.split("_")[1] : eachNode?.data[OPTION_CHAIN_GRID_INDEX.PUT_SEGMENT_ID_TOKE];
      objRenderedScrips[id2 + '_PUT'] = eachNode?.data;
      arrWithKeys?.push(id2);
      arrRowsVisibleInPut?.push({ exchangeId: Number(eachNode?.data[OPTION_CHAIN_GRID_INDEX.PUT_SEGMENT_ID_TOKE]?.split('_')[0]), token: Number(eachNode?.data[OPTION_CHAIN_GRID_INDEX.PUT_SEGMENT_ID_TOKE]?.split('_')[1]), isSubscribe: true });
      return { exchangeId: Number(eachNode?.data[OPTION_CHAIN_GRID_INDEX.CALL_SEGMENT_ID_TOKEN]?.split('_')[0]), token: Number(eachNode?.data[OPTION_CHAIN_GRID_INDEX.CALL_SEGMENT_ID_TOKEN]?.split('_')[1]), isSubscribe: true };
    });
    setObjGridVisibleRows({ ...objRenderedScrips });
    const arrRowsVisible = [...arrRowsVisibleInCall, ...arrRowsVisibleInPut];
    setObjScripsRequest(prevState => {
      let objCopy = prevState;
      let subscribeScrips = []; // This array is a non repeated subscrips for subscription
      let unSubScribeScrips = []; // this array is scrips of unsubscription]
      arrRowsVisible?.forEach((eachScrip) => {
        let id = eachScrip?.exchangeId + '_' + eachScrip?.token;
        if (!objCopy[id]) {
          objCopy[id] = true;
          subscribeScrips?.push(eachScrip);
        }
      });
      Object?.keys(objCopy)?.forEach(key => {
        if (!arrWithKeys.includes(key)) {
          let obj = {
            exchangeId: Number(key?.split('_')[0]),
            token: Number(key?.split('_')[1]),
            isSubscribe: false
          };
          unSubScribeScrips?.push(obj);
          delete objCopy[key];
        }
      });
      sendTouchlineRequest(unSubScribeScrips);
      sendTouchlineRequest(subscribeScrips);
      return { ...objCopy };
    });
    useDebounce(() => {
      handleVisibleRowsLastTick(arrRowsVisible);
    }, 400);

    return arrRowsVisible;
  }, [handleVisibleRowsLastTick]);

  /*
Below function is the native event of grid which will be called once the scrollings ends not while scrolling
*/
  const handleScrollEnd = useCallback(() => {
    //props will give rendered rows make changes to que that function accepts props also

    useDebounce(() => {
      globalFunctionsEventDispatcher([GLOBAL_FUNCTION_TYPES.SCROLLING_OPTION_CHAIN]);// this function produces a event to document to close popover in case its open
      queue.enqueue(applySpotPriceToVisibleRows);
      useDebounce(() => {
        queue.enqueue(handleVisibleRowsSubscription);
      }, 300);
    }, 200);

  }, [applySpotPriceToVisibleRows, handleVisibleRowsSubscription, queue]);

  const [isExpanded, setIsExpanded] = useState(false);

  /*
  below hook is used for communication of calls between componenets without using
  any states
  */
  useWFHMessages('global_function', (e) => {
    if (e?.detail?.length > 0) {
      e?.detail?.forEach((eachType) => {
        switch (eachType) {
          case GLOBAL_FUNCTION_TYPES.EXPANDING_MARKET_OVERVIEW:
            setIsExpanded(true);
            useDebounce(() => {
              OPTION_CHAIN_SCREEN_REF?.current?.api?.ensureColumnVisible(OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE?.toString(), 'middle');
            }, 300);
            break;
          case GLOBAL_FUNCTION_TYPES.COLLASPSING_MARKET_OVERVIEW:
            setIsExpanded(false);
            useDebounce(() => {
              OPTION_CHAIN_SCREEN_REF?.current?.api?.ensureColumnVisible(OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE?.toString(), 'middle');
            }, 300);
            break;
          case GLOBAL_FUNCTION_TYPES?.CLOSING_OPTION_CHAIN:
            setObjScripsRequest({});
            setObjGridVisibleRows({});
            break;
          default:
            break;
        }
      });
    }
  });

  useEffect(() => {
    setContractDropdown(_optionContract(OPTION_CHAIN.CALL_PUT).data);
  }, []);

  /*
Below function will know spot price row by doing binary search instead a whole loop as data is sorted
*/
  const findClosestArrayOfSpotPrice = useCallback((isSpot = true, value) => {
    //if spot price present then only scroll to index but only once or on date change call or new scrip search
    if (isSpot) {
      if (!spotPrice?.toString()) {
        let button = document.getElementById('optionchain-strikeprice');
        if (button) {
          button.innerHTML = twoDecimalValueChecker(arrStrikePrices[0]) ?? 0.00;
          button.value = arrStrikePrices[0] ?? 0.00;
        }
      }
    }
    const target = isSpot ? parseInt(spotPrice) : value;
    const getValueAtIndex = (array) => parseInt(array[OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE]);
    let arrMemoMisedOptionChainData = [];
    OPTION_CHAIN_SCREEN_REF?.current?.api?.forEachNode((node) => {
      arrMemoMisedOptionChainData?.push(node?.data);
    });
    let left = 0;
    let right = arrMemoMisedOptionChainData?.length - 1;

    let closestArray = null;
    let closestDiff = Infinity;
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      const midValue = getValueAtIndex(arrMemoMisedOptionChainData[mid]);

      if (midValue === target) {
        setOnLoadStrikePrice(arrMemoMisedOptionChainData[mid][OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE]);
        let button = document.getElementById('optionchain-strikeprice');
        if (button) {
          button.innerHTML = twoDecimalValueChecker(arrMemoMisedOptionChainData[mid][OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE]);
          button.value = arrMemoMisedOptionChainData[mid][OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE];
        }
        OPTION_CHAIN_SCREEN_REF?.current?.api?.ensureIndexVisible(arrMemoMisedOptionChainData[mid][OPTION_CHAIN_GRID_INDEX.ID_FOR_OPTION_CHAIN_GRID], 'middle');
      }
      const diff = Math.abs(midValue - target);

      if (diff < closestDiff) {
        closestDiff = diff;
        closestArray = arrMemoMisedOptionChainData[mid];
      }

      if (midValue < target) {
        left = mid + 1;
      } else {
        right = mid - 1;
      }

    }
    if (closestArray && closestArray[OPTION_CHAIN_GRID_INDEX.ID_FOR_OPTION_CHAIN_GRID] > 0) {
      setTimeout(() => {
        setOnLoadStrikePrice(closestArray[OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE]);
        let button = document.getElementById('optionchain-strikeprice');
        if (button) {
          button.innerHTML = twoDecimalValueChecker(closestArray[OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE]);
          button.value = closestArray[OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE];
        }
        OPTION_CHAIN_SCREEN_REF?.current?.api?.ensureIndexVisible(closestArray[OPTION_CHAIN_GRID_INDEX.ID_FOR_OPTION_CHAIN_GRID], 'middle');
      }, 200);
    }
    // } else { }
  }, [spotPrice, arrStrikePrices]);

  /*
Below function sets the spot price last tick of selected scrip
*/
  const handleLastTickOfSelectedOption = useCallback(() => {
    const bestFiveLastTickData = dispatch(getWfhBestFiveLastTick());
    if (bestFiveLastTickData[`${objOptionChainPayload?.mappedId}_${objOptionChainPayload?.assetToken}`]?.length > 0) {
      setSpotPrice(bestFiveLastTickData[`${objOptionChainPayload?.mappedId}_${objOptionChainPayload?.assetToken}`][BEST5_INDEX.LAST_TRADE_PRICE]);
    }
  }, [objOptionChainPayload, dispatch]);

  /*
Below function is a native Event of grid that gets called only once that the data is loaded on load
succesfully what ever logics we on load canbe writeen here instead using useEffect
*/
  const onFirstDataRendered = useCallback(() => { // Ameersha N --- this is grid functionality event when ever the data first load into dom this event weill be triggered we can use this on load instead of useEffect for neccessary functions
    handleLastTickOfSelectedOption();
    setTimeout(() => {
      OPTION_CHAIN_SCREEN_REF?.current?.api?.ensureColumnVisible(OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE?.toString(), 'middle');
    }, 1000);
    queue.enqueue(findClosestArrayOfSpotPrice);
  }, [findClosestArrayOfSpotPrice, handleLastTickOfSelectedOption, queue]);
  ;

  useWFHMessages('bestfive', (e) => {
    const detail = [...e.detail];
    if (detail[BEST5_INDEX.SEGMENT_ID] === objOptionChainPayload?.mappedId && detail[BEST5_INDEX.TOKEN] === objOptionChainPayload?.assetToken) {
      setSpotPrice(_ => detail[BEST5_INDEX.LAST_TRADE_PRICE]);
    }
  });

  useWFHMessages('touchline', (e) => {
    handleLiveData(e?.detail);
  });

  /*
Below function added and removes columns using the api of grid instead of state so renreders can be avoided
*/
  const toggleCallPutGrids = useCallback((e) => {
    setStrActiveOption(e);
    setContractDropdown(_optionContract(e).data);
    if (e === OPTION_CHAIN.CALL_PUT) {
      let filteredColumns = columnDefs;
      setSelectedContract('ALL');
      filteredColumns?.forEach((eachColumn) => {
        if (eachColumn.flex === 1) {
          eachColumn['flex'] = 0;
        }
      });

      OPTION_CHAIN_SCREEN_REF.current.api.setGridOption("columnDefs", filteredColumns);
      OPTION_CHAIN_SCREEN_REF?.current?.api?.ensureColumnVisible(OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE?.toString(), 'middle');
    } else if (e === OPTION_CHAIN.CALL) {
      let filteredColumns = columnDefs?.filter((eachColumn) => (eachColumn?.field === OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE?.toString() || eachColumn?.type === e));
      filteredColumns?.forEach((eachColumn) => {
        eachColumn['flex'] = 1;
      });
      OPTION_CHAIN_SCREEN_REF.current.api.setGridOption("columnDefs", filteredColumns);
      OPTION_CHAIN_SCREEN_REF?.current?.api?.ensureColumnVisible(OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE?.toString(), 'start');
    } else if (e === OPTION_CHAIN.PUT) {
      let filteredColumns = columnDefs?.filter((eachColumn) => eachColumn?.field === OPTION_CHAIN_GRID_INDEX?.STRIKE_PRICE?.toString() || eachColumn?.type === e);
      filteredColumns?.forEach((eachColumn) => {
        eachColumn['flex'] = 1;
      });
      OPTION_CHAIN_SCREEN_REF.current.api.setGridOption("columnDefs", filteredColumns);
      OPTION_CHAIN_SCREEN_REF?.current?.api?.ensureColumnVisible(OPTION_CHAIN_GRID_INDEX.STRIKE_PRICE?.toString(), 'end');
    } else {
      //do not do any thing here sit silently
    }
  }, [columnDefs]);

  /*
Below function gets called when user selects any date from dropdown and do's the logic need for date changes like new grid on open
*/
  const handleExpiryDates = useCallback((date) => {
    if (selectedExpiry === date) {
      return;
    }
    setSelectedExpiry(date);
    // toggle data
    dispatch(updateOptionChainDummyData([]));
    dispatch(updateOptionChainLoading(true));
    dateref.current.value = date;
    let symbol = modifySymbol(objOptionChainPayload?.symbol);
    dispatch(updateDateSelection({ expiryDate: date, symbol: symbol }))?.then(() => {
      handleLastTickOfSelectedOption();
      setTimeout(() => {
        // here previous scrips shoudl be unsubscribed
        // once scrolled automatically happens everything
        findClosestArrayOfSpotPrice(true);
        setTimeout(() => {
          applySpotPriceToVisibleRows();
          handleVisibleRowsSubscription();
        }, 300);
      }, 500);
    });

  }, [objOptionChainPayload, selectedExpiry, findClosestArrayOfSpotPrice, applySpotPriceToVisibleRows, handleVisibleRowsSubscription, dispatch, handleLastTickOfSelectedOption]);

  /*
Below functions gets called when grid to be scrolled based on the strike price provided
*/
  const handleStrikePrices = useCallback((event) => {
    findClosestArrayOfSpotPrice(false, parseInt(JSON?.parse(event)?.value));
  }, [findClosestArrayOfSpotPrice]);

  /*
Below function handles Contracts like OTM,ATM,ITM when CALL oR PUts are toggled
*/
  const handleContracts = useCallback((optionType, strPosition) => {
    const updatedStrikePriceArr = arrStrikePrices;
    let indexToScroll = findIndexForValueBetween(updatedStrikePriceArr, spotPrice);
    if (optionType === OPTION_CHAIN.CALL) {
      OPTION_CHAIN_SCREEN_REF?.current?.api.ensureIndexVisible(indexToScroll, strPosition);
    } else if (optionType === OPTION_CHAIN.PUT) {
      OPTION_CHAIN_SCREEN_REF?.current?.api.ensureIndexVisible(indexToScroll, strPosition);
    } else if (optionType === 'ALL') {
      OPTION_CHAIN_SCREEN_REF?.current?.api.ensureIndexVisible(indexToScroll, strPosition);
    }
  }, [arrStrikePrices, spotPrice]);

  const scrollWRTContracts = useCallback((e) => {
    if (strActiveOption === OPTION_CHAIN.CALL) {
      switch (e) {
        case OPTION_CHAIN_DROPDOWN.ATM: {
          setSelectedContract(OPTION_CHAIN_DROPDOWN.ATM);
          handleContracts('ALL', 'middle');
          break;
        }
        case OPTION_CHAIN_DROPDOWN.ITM: {
          setSelectedContract(OPTION_CHAIN_DROPDOWN.ITM);
          handleContracts(OPTION_CHAIN.CALL, 'bottom');
          break;
        }
        case OPTION_CHAIN_DROPDOWN.OTM: {
          setSelectedContract(OPTION_CHAIN_DROPDOWN.OTM);
          handleContracts(OPTION_CHAIN.CALL, 'top');
          break;
        }
        default: {
          setSelectedContract('ALL');
          break;
        }
      }
    } else if (strActiveOption === OPTION_CHAIN.PUT) {
      switch (e) {
        case OPTION_CHAIN_DROPDOWN.ATM: {
          setSelectedContract(OPTION_CHAIN_DROPDOWN.ATM);
          handleContracts('ALL', 'middle');
          break;
        }
        case OPTION_CHAIN_DROPDOWN.ITM: {
          setSelectedContract(OPTION_CHAIN_DROPDOWN.ITM);
          handleContracts(OPTION_CHAIN.PUT, 'top');
          break;
        }
        case OPTION_CHAIN_DROPDOWN.OTM: {
          setSelectedContract(OPTION_CHAIN_DROPDOWN.OTM);
          handleContracts(OPTION_CHAIN.PUT, 'bottom');
          break;
        }
        default: {
          setSelectedContract('ALL');
          break;
        }
      }
    } else if (strActiveOption === OPTION_CHAIN.CALL_PUT) {
      setSelectedContract('ALL');
      handleContracts('ALL', 'middle');
    }
  }, [strActiveOption, handleContracts]);

  const defaultColProps = useMemo(() => {
    return {
      suppressMovable: true,
      minWidth: 150
    };

  }, []);

  /*
  Below function moves grid to middle if not in middle and also sets the strike price in dropdown
  */
  const handleSpotPriceClick = useCallback((clickEvent) => {
    clickEvent?.preventDefault();
    findClosestArrayOfSpotPrice(false, spotPrice);
  }, [findClosestArrayOfSpotPrice, spotPrice]);

  /*
  On grid ready to load this function gets called so whatever we want before loading grid to dom can be done here
  */
  const onGridReady = useCallback(() => {
    handleLastTickOfSelectedOption();
  }, [handleLastTickOfSelectedOption]);

  const onGridPreDestroyed = useCallback(() => {
    // this function gets called when the grdi before gets destroyed means getting removed from dom
    /*
No code inside but can be used in feature as these functions are native events of grid
*/
  }, []);

  const rowSelection = useMemo(() => {
    return {
      mode: 'singleRow',
      checkboxes: false,
    };
  }, []);

  return (
    <>
      {
        isLoadingOptionChian && !isGuestUser() ?
          <Container className='p-0 py-5'>
            <Row className='g-0'>
              <Col className='d-flex justify-content-center align-items-center'>
                <LoginLoader spinner={isLoadingOptionChian} spinnerContent="Loading option Chain" />
              </Col>
            </Row>
          </Container> : null
      }

      {arrDistinctDates?.length > 0 && !isGuestUser() ?
        <Container fluid className='p-0 position-relative side-overwiew-option-chain-wrap'>
          <Row className='g-0 py-1 justify-content-center align-items-center'>
            <Col md={11}>
              <Col md={3} className='d-inline-flex align-items-center justify-content-center bdr-rgt'>
                <label className='font-medium color-subtext line-height-1 ms-2'>Expiry</label>
                <div className='screen-dropdown mx-2 w-50'>
                  <BzDropdown>
                    <Dropdown.Toggle className='btn-default text-truncate' ref={dateref} value={dateref?.current?.value}>{dateref?.current?.value ? dateref?.current?.value : objOptionChainPayload?.expiryDate?.length > 0 ? objOptionChainPayload?.expiryDate : arrDistinctDates[0]}</Dropdown.Toggle>
                    <Dropdown.Menu>
                      {
                        arrDistinctDates?.length > 0 && arrDistinctDates.map((date, index) => <Dropdown.Item key={index} value={date}
                          onClick={() => handleExpiryDates(date)}
                        >{date}</Dropdown.Item>)
                      }
                    </Dropdown.Menu>
                  </BzDropdown>
                </div>
              </Col>
              {
                objOptionChainPayload?.location !== OPTION_CHAIN.MULTI_LEG &&
                <Col md={3} className='d-inline-flex align-items-center justify-content-center bdr-rgt'>
                  <label className='font-medium color-subtext line-height-1 ms-2'>Type</label>
                  <div className='screen-dropdown mx-2 w-50'>
                    <BzDropdown>
                      <Dropdown.Toggle className='btn-default text-truncate'>{strActiveOption ? strActiveOption : OPTION_CHAIN.CALL_PUT}</Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item value={OPTION_CHAIN.CALL_PUT}
                          onClick={() => toggleCallPutGrids(OPTION_CHAIN.CALL_PUT)}
                        >{OPTION_CHAIN.CALL_PUT}</Dropdown.Item>
                        <Dropdown.Item value={OPTION_CHAIN.CALL}
                          onClick={() => toggleCallPutGrids(OPTION_CHAIN.CALL)}
                        >{OPTION_CHAIN.CALL}</Dropdown.Item>
                        <Dropdown.Item value={OPTION_CHAIN.PUT}
                          onClick={() => toggleCallPutGrids(OPTION_CHAIN.PUT)}
                        >{OPTION_CHAIN.PUT}</Dropdown.Item>
                      </Dropdown.Menu>
                    </BzDropdown>
                  </div>
                </Col>
              }
              <Col md={3} className='d-inline-flex align-items-center justify-content-center bdr-rgt position-relative'>
                <label className='font-medium color-subtext line-height-1 ms-2'>Strike</label>
                <div className='screen-dropdown mx-2 w-50'>
                  <BzDropdown
                    onSelect={handleStrikePrices}
                  >
                    <Dropdown.Toggle
                      defaultValue={arrStrikePrices[0] ?? 0.00}
                      className='btn-default text-truncate' id='optionchain-strikeprice'>{twoDecimalValueChecker(arrStrikePrices[0]) ?? 0.00}</Dropdown.Toggle>
                    <Dropdown.Menu>
                      {arrStrikePrices?.length > 0 && arrStrikePrices?.map((each, index) => {
                        return <Dropdown.Item key={index} eventKey={JSON.stringify({ value: each, key: index })} >{twoDecimalValueChecker(each)}</Dropdown.Item>;
                      })
                      }
                    </Dropdown.Menu>
                  </BzDropdown>
                </div>
              </Col>
              {
                objOptionChainPayload?.location !== OPTION_CHAIN.MULTI_LEG &&
                <Col md={3} className='d-inline-flex align-items-center justify-content-center'>
                  <label className='font-medium color-subtext line-height-1 ms-2'>Moneyness</label>
                  <div className='screen-dropdown mx-2 w-50'>
                    <BzDropdown>
                      <Dropdown.Toggle className='btn-default text-truncate'>{selectedContract}</Dropdown.Toggle>
                      <Dropdown.Menu>
                        {
                          contractDropdown?.map((contract, i) => {
                            return <Dropdown.Item key={i} value={contract?.length > 0 ? contract : ''} onClick={() => scrollWRTContracts(contract)}>{contract}</Dropdown.Item>;
                          })
                        }
                      </Dropdown.Menu>
                    </BzDropdown>
                  </div>
                </Col>
              }
            </Col>
            <Col md={1}>&nbsp;</Col>
          </Row>
          {/* <Col md={2}><BzButton onClick={() => handleOptionWatchlistCreation()}>Add to watchlist</BzButton></Col> */}

          <Row className='g-0'>
            <Col>
              <div className='d-flex flex-row justify-content-around align-items-center ag-header font-medium font-size3 color-text p-1'>
                {strActiveOption === OPTION_CHAIN.CALL ? <div className="font-medium font-size1 line-height-1">CALLS</div> :
                  strActiveOption === OPTION_CHAIN.PUT ? <div className="font-medium font-size1 line-height-1">PUTS</div> :
                    strActiveOption === OPTION_CHAIN.CALL_PUT ? <><div className="font-medium font-size1 line-height-1">CALLS</div><div className="font-medium font-size1 line-height-1">PUTS</div></> : null}
              </div>
            </Col>
          </Row>
          <Row className='g-0  justify-content-center'>
            <Col className='bdr'>
              <BzMasterAgGrid
                rowSelection={rowSelection}
                isFirstDataRendered={onFirstDataRendered}
                defaultColProps={defaultColProps}
                onGridReady={onGridReady}
                isCompress={true}
                arrColumnData={columnDefs}
                arrRowData={arrMemoMisedOptionChainData}
                className={`ag-theme-alpine ${objOptionChainPayload?.location === OPTION_CHAIN.GLOBAL_SEARCH ? 'option-chain-grid-height-globle-search' : objOptionChainPayload?.location === OPTION_CHAIN.SIDE_OVERVIEW ? halfHeight ? 'side-overview-option-agrid-halfheight' : 'side-overview-option-agrid-fullheight' : isExpanded ? 'option-chain-grid-height-expand' : 'option-chain-grid-height'}`}
                style={{ height: "100px", width: `${objOptionChainPayload?.location === OPTION_CHAIN.SIDE_OVERVIEW ? '' : '1000px'}` }}
                ref={OPTION_CHAIN_SCREEN_REF}
                getRowId={getRowId}
                onBodyScrollEnd={handleScrollEnd}
                rowHeight={20}
                rowBuffer={1}
                suppressRowHoverHighlight={true}
                debounceVerticalScrollbar={true}
                tooltipInteraction={true}
                onGridPreDestroyed={onGridPreDestroyed}
              />
            </Col>
            <div className='indicator stick-bottom cursor-pointer' onClick={(e) => handleSpotPriceClick(e)}>
              <span className='title font-regular text-nowrap'>
                <span className='font-regular font-size1'>Spot Price: </span> <span>{spotPrice}</span>
              </span>
            </div>
          </Row>
          {
            arrLegDataForOptionChain.length > 0 &&
            <OptionChainToaster optionChainRef={OPTION_CHAIN_SCREEN_REF} sideOverview={sideOverview} />
          }
        </Container >
        : <div className='font-regular font-size2 color-nodata zero-state-alerts zero-state-alerts-xxl'>No Data Found</div>}
    </>
  );

};


export default memo(OptionChainGrid);